import type { ReactElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { useFloating, autoUpdate, offset as floatingOffset } from '@floating-ui/react-dom';
import classNames from 'classnames';
import styles from './NotifyPopup.module.css';
import { useEffect, useRef } from 'react';

export interface NotifyPopupProps {
  type: 'success' | 'warning';
  show: boolean;
  children: ReactElement;
  content: ReactElement | string;
  duration?: number;
  onHide: () => void;
  containerProps?: React.ComponentPropsWithoutRef<'div'> & {
    'data-testid': string;
  };
}

export const NotifyPopup = ({
  type = 'success',
  children,
  content,
  show,
  onHide,
  duration = 3000,
  containerProps,
}: NotifyPopupProps) => {
  const { x, y, reference, floating, strategy } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: 'top-start',
    middleware: [
      floatingOffset(({ rects }) => ({
        mainAxis: -(rects.floating.height + 15),
        crossAxis: 15,
      })),
    ],
  });

  const timeoutRef = useRef<number>();

  useEffect(() => {
    if (show) {
      timeoutRef.current = window.setTimeout(onHide, duration);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);

        if (show) {
          onHide();
        }
      }
    };
  }, [show, timeoutRef]);

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            ref={floating}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            className={classNames(styles.general, styles[type], 'text-sm flex-1')}
          >
            <Icon id={type === 'success' ? IconID.CHECK : IconID.CLOSE_CROSS} color={IconColor.WHITE} size={11} />{' '}
            {content}
          </motion.div>
        )}
      </AnimatePresence>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <div {...containerProps} ref={reference}>
        {children}
      </div>
    </>
  );
};
